/**
 *  @swagger
 *  definitions:
 *    RegattaPaymentParams:
 *      type: object
 *      properties:
 *        RegattaId:
 *          description: ID of the associated Regatta
 *          type: string
 *        RegattaPaymentDiscountId:
 *          description: ID of the associated RegattaPaymentDiscount
 *          type: string
 *        PaymentId:
 *          description: PaymentId of the RegattaPayment
 *          type: string
 *        PaymentIntent:
 *          description: PaymentIntent of the RegattaPayment
 *          type: string
 *        Status:
 *          description: Status of the RegattaPayment
 *          $ref: '#/components/enums/RegattaPaymentStatus'
 *
 *  @swagger
 *  definitions:
 *    RegattaPaymentCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaPaymentParams'
 *        - type: object
 *          required:
 *            - RegattaId
 *            - PaymentId
 *            - PaymentIntent
 *
 *  @swagger
 *  definitions:
 *    RegattaPaymentQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the RegattaPayment
 *              type: string
 *        - $ref: '#/definitions/RegattaPaymentParams'
 *
 *  @swagger
 *  definitions:
 *    RegattaPaymentUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaPaymentQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    RegattaPayment:
 *      allOf:
 *        - $ref: '#/definitions/RegattaPaymentCreateParams'
 *        - $ref: '#/definitions/RegattaPaymentUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      RegattaPayment:
 *        allOf:
 *          - $ref: '#/definitions/RegattaPayment'
 *          - type: object
 *            properties:
 *              Regatta:
 *                description: Regatta of the RegattaPayment
 *                $ref: '#/components/schemas/Regatta'
 *              RegattaPaymentDiscount:
 *                description: RegattaPaymentDiscount of the RegattaPayment
 *                $ref: '#/components/schemas/RegattaPaymentDiscount'
 */

// Enums
import {
  RegattaPaymentStatus,
} from '../enums';

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  RegattaId: string;
  RegattaPaymentDiscountId: null | string;
};

export type $Params = {
  PaymentId: string;
  PaymentIntent: string;
  Status: RegattaPaymentStatus;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  Regatta?: $Model<'Regatta'>;
  RegattaPaymentDiscount?: $Model<'RegattaPaymentDiscount'>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  PaymentId: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  PaymentIntent: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Status: {
    allowNull: false,
    type: Sequelize.ENUM(...Object.values(RegattaPaymentStatus)),
    validate: {
      notEmpty: true,
    },
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'RegattaPayment'> => {
  const RegattaPayment = stack.define(
    'RegattaPayment',
    fields(Sequelize),
    {
      tableName: 'RegattaPayments',
    },
  );

  RegattaPayment.associate = (models: $ModelClasses) => {
    models.RegattaPayment.belongsTo(
      models.Regatta,
      {
        as: 'Regatta',
        foreignKey: 'RegattaId',
      },
    );
    models.RegattaPayment.belongsTo(
      models.RegattaPaymentDiscount,
      {
        as: 'RegattaPaymentDiscount',
        constraints: false,
        foreignKey: 'RegattaPaymentDiscountId',
      },
    );
  };

  RegattaPayment.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return RegattaPayment;
});
